import { Button, Tabs, Tooltip, Typography } from "antd";
import {
  createPlaceholderStepId,
  TriggerStepCreate,
  When,
  when2String,
} from "@dots-projects/types";
import {
  addSteps,
  saveDraft,
  selectNode,
  selectTransition,
} from "./reducer/actionCreators";
import { getSelectedNode, getSelectedTransition } from "./reducer/stateUtils";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import "./ConditionalForm.css";
import { BranchForm } from "./BranchForm";
import { nanoid } from "nanoid";
import clsx from "clsx";
import { CloseCircleOutlined } from "@ant-design/icons";

const { Title } = Typography;

/**
 * Edit form displayed when a conditional node (Diamond) is selected.
 * Modal content depends on the step type
 */
export function ConditionalForm({ step }: { step: TriggerStepCreate }) {
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const selectedTransition = getSelectedTransition(state);
  const isPersisting = state.editor.isPersisting === "save-draft";

  const add = () => {
    const newPlaceholderId = createPlaceholderStepId(
      `${step.type}-${nanoid(6)}`
    );
    let channelId = "$channelId";
    if (step.configs && "channelId" in step.configs && step.configs.channelId) {
      channelId = step.configs.channelId;
    }
    const when: When = {
      userResponse: {
        type: "message",
        userId: "{{user.id}}",
        channelId: channelId,
      },
    };

    dispatch(
      addSteps(
        [
          {
            type: "idle",
            id: newPlaceholderId,
            transitions: [],
          },
        ],
        {
          parentStepId: step.id,
          when,
        }
      )
    );
    dispatch(selectTransition(step.id, newPlaceholderId, when2String(when)));
    dispatch(saveDraft(onPersist));
  };

  const parentStep = state.trigger.current.steps.find((s) =>
    s.transitions.map((t) => t.targetStepId).includes(step.id)
  );
  const parentIsTestCaptcha = parentStep?.type === "test_captcha";

  return (
    <div>
      {step.type === "send_survey" && (
        <>
          <Title level={5}>Survey options</Title>
          {step.configs.options.map((op) => {
            if (typeof op === "string") {
              return <p>{op}</p>;
            } else {
              return (
                <p>
                  {op.emoji} {op.label}
                </p>
              );
            }
          })}
          <Button
            type="primary"
            className="w-full mt-6"
            onClick={() => {
              dispatch(selectNode(step.id));
            }}
          >
            <span>Edit Survey</span>
          </Button>
        </>
      )}

      {parentIsTestCaptcha && (
        <>
          <Title level={5}>Conditional</Title>
          <Tabs
            activeKey={createTabKey(
              selectedTransition?.data?.targetStepId,
              selectedTransition?.data?.whenAsString
            )}
            onChange={(tabKey) => {
              const [targetStepId, whenAsString] = tabKey.split("::");
              dispatch(selectTransition(step.id, targetStepId, whenAsString));
            }}
            size="small"
          >
            {step.transitions.map((t, idx) => (
              <Tabs.TabPane
                key={createTabKey(t.targetStepId, when2String(t.when))}
                tab={t.name ? t.name : `Branch ${idx + 1}`}
              >
                <BranchForm
                  idx={idx}
                  transition={t}
                  step={step}
                  key={t.targetStepId}
                  readonly={true}
                />
              </Tabs.TabPane>
            ))}
          </Tabs>
        </>
      )}

      {(step.type === "send_message" || step.type === "idle") &&
        !parentIsTestCaptcha && (
          <>
            <div className="flex justify-between">
              <Title level={5}>Conditional</Title>
              <Tooltip title="Close without saving" placement="top">
                <CloseCircleOutlined
                  style={{ fontSize: "20px", color: "#6C6C6C" }}
                  onClick={() => {
                    dispatch({ type: "unselect" });
                  }}
                />
              </Tooltip>
            </div>
            {step.configs && "delaySeconds" in step.configs ? null : (
              <div style={{ marginBottom: 5, marginTop: 15 }}>
                <div
                  onClick={add}
                  className={clsx("cursor-pointer font-medium	", {
                    "pointer-events-none	opacity-50": isPersisting,
                  })}
                  style={{ color: "#3b3b3b" }}
                >
                  + Add branch
                </div>
              </div>
            )}
            <Tabs
              activeKey={createTabKey(
                selectedTransition?.data?.targetStepId,
                selectedTransition?.data?.whenAsString
              )}
              onChange={(tabKey) => {
                const [targetStepId, whenAsString] = tabKey.split("::");
                dispatch(selectTransition(step.id, targetStepId, whenAsString));
              }}
              size="small"
            >
              {step.transitions.map((t, idx) => (
                <Tabs.TabPane
                  key={createTabKey(t.targetStepId, when2String(t.when))}
                  tab={t.name ? t.name : `Branch ${idx + 1}`}
                >
                  <BranchForm
                    idx={idx}
                    transition={t}
                    step={step}
                    key={t.targetStepId}
                  />
                </Tabs.TabPane>
              ))}
            </Tabs>
          </>
        )}
    </div>
  );
}

function createTabKey(targetStepId: string, whenAsString?: string) {
  let key = targetStepId;
  if (whenAsString) {
    key += "::" + whenAsString;
  }
  return key;
}
